import ee from '../../services/earth-engine';
/**
 * @function add_lyzengaPredictors
 * @description Computes the Lyzenga log-transformed predictors (lnBand1, lnBand2, lnBand3) from
 * the three selected bands [band0, band1, band2], used as the regression inputs for Model 1.
 * 1. Clamps each band to a small positive epsilon, since log() is undefined for values <= 0.
 * 2. Takes the natural log of each clamped band.
 * 3. Renames the three results to lnBand1/lnBand2/lnBand3 and adds them to the image.
 * @param {ee.Image} image - Input multispectral image with the required bands.
 * @param {Array<string>} bands - [band0, band1, band2] names to log-transform.
 * **Called by:
 * @see {model1Apply}
 * @see {reprocessCharts}
 * @memberof module:Bathymetry
 * @returns {ee.Image} Input image with lnBand1, lnBand2, lnBand3 added.
 */
export const add_lyzengaPredictors = (image, bands) => {
  const EPS = ee.Number(1e-6);
  const ln1 = image.select(bands[0]).max(EPS).log().rename('lnBand1');
  const ln2 = image.select(bands[1]).max(EPS).log().rename('lnBand2');
  const ln3 = image.select(bands[2]).max(EPS).log().rename('lnBand3');
  return image.addBands([ln1, ln2, ln3], null, true);
};
/**
 * @function fitLyzengaSafe
 * @description Fits the Lyzenga multiple linear regression (depth = c + a*lnBand1 + b*lnBand2 + d*lnBand3)
 * against known training points, returning safe zeroed coefficients when fitting is not possible.
 * 1. Filters the sample collection to features with all required predictors and 'or_depth' present.
 * 2. If there are samples, runs a 4-predictor linear regression; otherwise uses a null coefficients dictionary.
 * 3. Flags whether the fit succeeded (ok) and falls back to all-zero coefficients when it didn't.
 * 4. Unpacks the fitted coefficient array into named fields (c, a, b, d) alongside n and ok.
 * @param {ee.FeatureCollection} fcSamples - Sampled points with 'constant', 'lnBand1', 'lnBand2', 'lnBand3', 'or_depth'.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {ee.Dictionary} Coefficients { c, a, b, d, n, ok } (ok=0 when the fit failed or had no samples).
 */
export const fitLyzengaSafe = (fcSamples) => {
  const req = ['constant', 'lnBand1', 'lnBand2', 'lnBand3', 'or_depth'];
  const fc = ee.FeatureCollection(fcSamples).filter(ee.Filter.notNull(req));
  const n = fc.size();
  const lr = ee.Dictionary(
    ee.Algorithms.If(
      n.gt(0),
      fc.reduceColumns({
        reducer: ee.Reducer.linearRegression({ numX: 4, numY: 1 }),
        selectors: ['constant', 'lnBand1', 'lnBand2', 'lnBand3', 'or_depth'],
      }),
      ee.Dictionary({ coefficients: null }),
    ),
  );
  const coeffs = lr.get('coefficients');
  const ok = ee.Number(ee.Algorithms.If(ee.Algorithms.IsEqual(coeffs, null), 0, 1));
  const coefArr = ee.Array(ee.Algorithms.If(ok.eq(1), coeffs, [[0], [0], [0], [0]]));
  const out = ee.Dictionary({
    c: coefArr.get([0, 0]),
    a: coefArr.get([1, 0]),
    b: coefArr.get([2, 0]),
    d: coefArr.get([3, 0]),
    n: n,
    ok: ok,
  });
  return out;
};

/**
 * @function applyLyzenga
 * @description Applies the fitted Lyzenga coefficients to the log-transformed bands to
 * produce the predicted depth band (pred_depth = c + a*lnBand1 + b*lnBand2 + d*lnBand3).
 * @param {ee.Image} image - Image already containing lnBand1, lnBand2, lnBand3 (see add_lyzengaPredictors).
 * @param {Dictionary} coefDict - Coefficients dictionary, must contain c, a, b, d.
 * @see {@link add_lyzengaPredictors} - produces the lnBand1/2/3 predictors this function consumes.
 * @see {@link fitLyzengaSafe} - generates coefDict.
 * **Called by:
 * @see {model1Apply}
 * @memberof module:Bathymetry
 * @returns {ee.Image} Input image with the pred_depth band added.
 */
export const applyLyzenga = (image, coefDict) => {
  return image.addBands(
    image
      .expression('c + a*ln1 + b*ln2 + d*ln3', {
        c: ee.Number(coefDict.c || 0),
        a: ee.Number(coefDict.a || 0),
        b: ee.Number(coefDict.b || 0),
        d: ee.Number(coefDict.d || 0),
        ln1: image.select('lnBand1'),
        ln2: image.select('lnBand2'),
        ln3: image.select('lnBand3'),
      })
      .toFloat()
      .rename('pred_depth'),
    null,
    true,
  );
};
